/**
 * Key Ingredients Accordion
 * - Ensures only one accordion item is open at a time
 * - Smooth open/close animation (same as menu/collapsible)
 */
class KeyIngredientsAccordion {
  constructor(container) {
    this.container = container || document.querySelector('.key-ingredients-accordion');
    if (!this.container) return;

    this.details = this.container.querySelectorAll('details');
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.animationDuration = 360;
    this.init();
  }

  init() {
    this.details.forEach((detail) => {
      const summary = detail.querySelector('summary');
      const content = detail.querySelector('[data-content]');
      if (!summary) return;
      // Skip animation for items without content - use default toggle
      if (!content) return;

      detail._accordion = { summary, content, animation: null, isClosing: false, isExpanding: false };

      summary.addEventListener('click', (e) => this.handleClick(e, detail));
    });
  }

  handleClick(e, detail) {
    e.preventDefault();
    const { summary, content, animation, isClosing, isExpanding } = detail._accordion;

    if (isClosing || isExpanding) return;

    if (detail.open) {
      this.collapse(detail);
    } else {
      // Close others and open this one (animations run in parallel)
      this.details.forEach((d) => {
        if (d !== detail && d.open) this.collapse(d);
      });
      this.expand(detail);
    }
  }

  collapse(detail) {
    const { summary, content, animation } = detail._accordion;
    if (!content) return;

    detail._accordion.isClosing = true;
    detail.style.overflow = 'hidden';
    const startHeight = `${detail.offsetHeight}px`;
    const endHeight = `${summary.offsetHeight}px`;

    if (animation) animation.cancel();

    if (!this.prefersReducedMotion.matches) {
      detail._accordion.animation = detail.animate(
        { height: [startHeight, endHeight] },
        { duration: this.animationDuration, easing: 'ease' }
      );
      detail._accordion.animation.onfinish = () => this.onAnimationFinish(detail, false);
      detail._accordion.animation.oncancel = () => (detail._accordion.isClosing = false);
    } else {
      this.onAnimationFinish(detail, false);
    }
  }

  expand(detail) {
    const { summary, content, animation } = detail._accordion;
    if (!content) return;

    detail.style.overflow = 'hidden';
    detail.style.height = `${detail.offsetHeight}px`;
    detail.open = true;
    requestAnimationFrame(() => {
      detail._accordion.isExpanding = true;
      const startHeight = detail.style.height;
      const endHeight = `${summary.offsetHeight + content.offsetHeight}px`;

      if (animation) detail._accordion.animation?.cancel();

      if (!this.prefersReducedMotion.matches) {
        detail._accordion.animation = detail.animate(
          { height: [startHeight, endHeight] },
          { duration: this.animationDuration, easing: 'ease' }
        );
        detail._accordion.animation.onfinish = () => this.onAnimationFinish(detail, true);
        detail._accordion.animation.oncancel = () => (detail._accordion.isExpanding = false);
      } else {
        this.onAnimationFinish(detail, true);
      }
    });
  }

  onAnimationFinish(detail, open) {
    detail.open = open;
    detail._accordion.animation = null;
    detail._accordion.isClosing = false;
    detail._accordion.isExpanding = false;
    detail.style.height = '';
    detail.style.overflow = '';
  }
}

// Initialize all accordion containers when DOM is ready
function initKeyIngredientsAccordions() {
  document.querySelectorAll('.key-ingredients-accordion').forEach((container) => {
    new KeyIngredientsAccordion(container);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initKeyIngredientsAccordions);
} else {
  initKeyIngredientsAccordions();
}
